import type { ComponentTheme } from '../types/component-theme';

import { AiChatMessageVariants } from './constants/ai-chat-message';
import { ThemeVariants, type ThemeVariant } from './constants/theme-variants';
type CodeLanguageStyle = {
    language: string;
    label: string;
    backgroundColor?: Partial<Record<ThemeVariant, string>>;
};

const codeLanguages = Object.freeze<CodeLanguageStyle[]>([
    {
        language: '',
        label: 'Code',
        backgroundColor: {
            [ThemeVariants.Light]: 'bg-violet-100/60',
            [ThemeVariants.Dark]: 'bg-slate-950/55'
        }
    },
    {
        language: 'csharp',
        label: 'C#',
        backgroundColor: {
            [ThemeVariants.Light]: 'bg-white/70',
            [ThemeVariants.Dark]: 'bg-slate-950/65'
        }
    },
    {
        language: 'markdown',
        label: 'Text',
        backgroundColor: {
            [ThemeVariants.Dark]: 'bg-blue-950/35'
        }
    }
]);
const codeLanguageLabelStyles = codeLanguages
    .map(({ language, label }) => {
        const selector = language ? '[&_code.language-'.concat(language, ']') : '[&_code:where([class^="language-"])]';

        return selector.concat(':before:content-["', label, '"]');
    })
    .join(' ');
const getCodeLanguageBackgroundStyles = (theme: ThemeVariant) =>
    codeLanguages
        .map(({ language, backgroundColor }) => {
            const color = backgroundColor?.[theme];
            const selector = language
                ? '[&_pre:has(code.language-'.concat(language, ')]')
                : '[&_pre:has(code:where([class^="language-"]))]';

            return color ? selector.concat(':', color) : '';
        })
        .filter(Boolean)
        .join(' ');

export default {
    slots: {
        root: 'flex',
        message: 'max-w-[75%] whitespace-pre-wrap wrap-break-word rounded-2xl px-4 py-3 text-sm leading-6',
        toggle:
            'mt-2 cursor-pointer rounded-t-none border-t border-current/15 pt-2 text-current hover:text-current ' +
            '[overflow-anchor:none]',
        markdown:
            'whitespace-normal [&_a]:underline [&_blockquote]:my-3 [&_blockquote]:border-l-2 ' +
            '[&_blockquote]:border-current/50 [&_blockquote]:pl-3 [&_blockquote]:italic [&_code]:rounded ' +
            '[&_code]:bg-black/20 [&_code]:px-1 [&_code]:py-0.5 [&_h1]:my-4 [&_h1]:text-xl [&_h1]:font-bold ' +
            '[&_h2]:my-3 [&_h2]:text-lg [&_h2]:font-bold [&_h3]:my-3 [&_h3]:font-semibold [&_h4]:my-2 ' +
            '[&_h4]:font-semibold [&_hr]:my-4 [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-2 ' +
            '[&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-xl ' +
            '[&_pre]:border [&_pre]:border-white/10 [&_pre]:bg-slate-950/55 [&_pre]:p-4 [&_pre]:font-mono ' +
            '[&_pre]:text-[0.8125rem] [&_pre]:leading-5 [&_pre]:text-slate-100 [&_pre]:shadow-inner ' +
            '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit [&_strong]:font-bold ' +
            '[&_pre_code]:before:mb-2 [&_pre_code]:before:block [&_pre_code]:before:font-sans ' +
            '[&_pre_code]:before:text-[0.6875rem] [&_pre_code]:before:font-semibold ' +
            '[&_pre_code]:before:tracking-wide [&_pre_code]:before:uppercase [&_pre_code]:before:opacity-60 ' +
            codeLanguageLabelStyles +
            ' [&_td[align=center]]:text-center [&_td[align=right]]:text-right [&_tbody_tr:nth-child(even)]:bg-black/5 ' +
            '[&_table]:my-3 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:border-collapse ' +
            '[&_td]:border [&_td]:border-current/25 [&_td]:px-3 [&_td]:py-2 [&_th]:border [&_th]:border-current/25 ' +
            '[&_th]:bg-black/10 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold ' +
            '[&_th[align=center]]:text-center [&_th[align=right]]:text-right [&_ul]:my-3 [&_ul]:list-disc ' +
            '[&_ul]:pl-6'
    },
    variants: {
        message: {
            [AiChatMessageVariants.Ai]: {
                root: 'justify-start'
            },
            [AiChatMessageVariants.User]: {
                root: 'justify-end'
            },
            [AiChatMessageVariants.AiInfo]: {
                root: 'justify-start'
            },
            [AiChatMessageVariants.UserInfo]: {
                root: 'justify-end'
            }
        }
    },
    compoundVariants: [
        {
            theme: ThemeVariants.Light,
            message: AiChatMessageVariants.Ai,
            class: {
                message: 'border border-violet-200 bg-violet-50 text-violet-950 shadow-sm',
                markdown:
                    '[&_blockquote]:border-violet-300 [&_code]:bg-violet-200/70 [&_pre]:border ' +
                    '[&_pre]:border-violet-200 [&_pre]:bg-violet-100/60 [&_pre]:text-violet-950 [&_pre]:shadow-none ' +
                    getCodeLanguageBackgroundStyles(ThemeVariants.Light) +
                    ' [&_td]:border-violet-200 [&_tbody_tr:nth-child(even)]:bg-violet-100/60 [&_th]:border-violet-200 ' +
                    '[&_th]:bg-violet-100'
            }
        },
        {
            theme: ThemeVariants.Light,
            message: AiChatMessageVariants.User,
            class: { message: 'bg-white text-slate-800' }
        },
        {
            theme: ThemeVariants.Light,
            message: AiChatMessageVariants.AiInfo,
            class: {
                message: 'border border-slate-300/70 bg-slate-50 text-slate-600',
                markdown:
                    '[&_blockquote]:border-slate-300 [&_code]:bg-slate-200/70 [&_pre]:border ' +
                    '[&_pre]:border-slate-200 [&_pre]:bg-slate-100/70 [&_pre]:text-slate-700 [&_pre]:shadow-none ' +
                    getCodeLanguageBackgroundStyles(ThemeVariants.Light) +
                    ' [&_td]:border-slate-200 [&_tbody_tr:nth-child(even)]:bg-slate-100/70 [&_th]:border-slate-200 ' +
                    '[&_th]:bg-slate-100'
            }
        },
        {
            theme: ThemeVariants.Light,
            message: AiChatMessageVariants.UserInfo,
            class: { message: 'border border-slate-300/70 bg-white text-slate-600' }
        },
        {
            theme: ThemeVariants.Dark,
            message: AiChatMessageVariants.Ai,
            class: {
                message: 'bg-blue-500/80 text-white',
                markdown: getCodeLanguageBackgroundStyles(ThemeVariants.Dark)
            }
        },
        {
            theme: ThemeVariants.Dark,
            message: AiChatMessageVariants.User,
            class: { message: 'bg-white text-slate-900' }
        },
        {
            theme: ThemeVariants.Dark,
            message: AiChatMessageVariants.AiInfo,
            class: {
                message: 'border border-white/10 bg-white/10 text-slate-200',
                markdown: getCodeLanguageBackgroundStyles(ThemeVariants.Dark)
            }
        },
        {
            theme: ThemeVariants.Dark,
            message: AiChatMessageVariants.UserInfo,
            class: { message: 'border border-white/10 bg-slate-100/90 text-slate-700' }
        }
    ]
} satisfies ComponentTheme;
